/**
 * Per-level state of the Ads Manager filter: the applied filter (persisted
 * per account × app × level in localStorage, TZ §8 + fix1), the popup, and what the funnel
 * dropdown / chips / empty state need. One provider per table page.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useApps, useCampaignStudioCountries, useEventTypesForFilter, useRulesV2 } from "@/lib/api-hooks";
import { loadSplit, saveSplit, splitAvailable as appSplitAvailable, splitStorageKey } from "@/lib/device-split";
import { ruleFitsApp } from "@/lib/rules-manager-filter";
import {
  clearStoredFilter,
  clearType,
  counts as countsOf,
  describeType,
  emptyFilter,
  isEmptyFilter,
  loadLevelFilter,
  pruneFilter,
  saveFilter,
  storageKey,
  type FilterLevel,
  toApiParam,
  type AdsManagerFilter,
  type FilterTab,
} from "@/lib/ads-manager-filter";
import { KW_AG_METRICS, METRICS } from "@/components/conditions/ConditionRow";
import type { Label } from "@/lib/types";
import { FilterModal, type MetricEnv } from "./FilterModal";
import { AdsFilterContext, type AdsFilterCtx as Ctx, type FilterGrain } from "./context";
import type { LabelOption } from "./MultiSelect";

const LEVEL: Record<FilterGrain, MetricEnv["level"]> = {
  campaigns: "campaign", ad_groups: "ad_group", keywords: "keyword",
};
// Storage level id per grain (fix1): stable routing ids, not tab captions.
const LEVEL_ID: Record<FilterGrain, FilterLevel> = {
  campaigns: "campaigns", ad_groups: "adgroups", keywords: "keywords",
};
const ENTITY_TYPE: Record<FilterGrain, "campaign" | "ad_group" | null> = {
  campaigns: "campaign", ad_groups: "ad_group", keywords: null,
};
// Level the rule picker counts on — the column's own definition, server-side.
const RULE_COUNT_LEVEL: Record<FilterGrain, "campaign" | "ad_group" | "keyword"> = {
  campaigns: "campaign", ad_groups: "ad_group", keywords: "keyword",
};

export function AdsFilterProvider({
  grain,
  accountId,
  appId,
  labels,
  children,
}: {
  grain: FilterGrain;
  /** Org public id — the storage key's account part. */
  accountId: string;
  appId: number | "all";
  /** The page's labels (with `applied_to`) — the same list the toolbar's
   *  label button and the table's Label column use. */
  labels: Label[];
  children: ReactNode;
}) {
  // One key per account × app × LEVEL (fix1): a filter set on campaigns
  // never reaches ad groups or keywords, each level keeps its own, and a
  // level's filter survives any navigation between levels.
  const level = LEVEL_ID[grain];
  const key = storageKey(accountId, appId, level);
  const [applied, setApplied] = useState<AdsManagerFilter>(() => loadLevelFilter(accountId, appId, level));
  const [modal, setModal] = useState<{ open: boolean; tab: FilterTab }>({ open: false, tab: "metrics" });

  // App, account or level switch → that key's filter.
  useEffect(() => { setApplied(loadLevelFilter(accountId, appId, level)); }, [key, accountId, appId, level]);

  // «Split by devices» — one flag per account × app shared by every level,
  // so it survives the drill from campaigns into a campaign's tabs.
  const splitKey = splitStorageKey(accountId, appId);
  const [splitStored, setSplitStored] = useState<boolean>(() => loadSplit(splitKey));
  useEffect(() => { setSplitStored(loadSplit(splitKey)); }, [splitKey]);
  const appsQ = useApps();
  const splitAvail = appId !== "all" && appSplitAvailable((appsQ.data ?? []).find((a) => a.id === appId));
  const splitOn = splitAvail && splitStored;
  const setSplit = useCallback((on: boolean) => { setSplitStored(on); saveSplit(splitKey, on); }, [splitKey]);

  const eventsQ = useEventTypesForFilter(appId === "all" ? null : appId);
  const countriesQ = useCampaignStudioCountries({ enabled: true });
  // fix2: the same list the toolbar's Rules ▾ (Rules Manager) reads —
  // `/api/rules` — asked once with the level so each rule carries how
  // many entities of this level it is applied to. Deleted rules are gone
  // (physical delete); paused ones stay, badged.
  const rulesQ = useRulesV2({ count_level: RULE_COUNT_LEVEL[grain] });

  const entityType = ENTITY_TYPE[grain];
  const labelOptions = useMemo<LabelOption[]>(() => {
    if (!entityType) return [];
    return labels
      .filter((l) => l.entity_type === entityType)
      .map((l) => ({ id: l.id, name: l.name, count: l.applied_to.filter((r) => r.entity_type === entityType).length }));
  }, [labels, entityType]);
  const labelName = useMemo(() => new Map(labels.map((l) => [l.id, l.name])), [labels]);
  const ruleOptions = useMemo<LabelOption[]>(() => {
    const rows = rulesQ.data ?? [];
    return rows
      // Rules of the current app; every rule of the account in "all apps"
      // (same predicate as the Rules Manager: an unattached rule fits any app).
      .filter((r) => appId === "all" || ruleFitsApp(r.resolved_app_id ?? r.app_id, appId))
      .map((r) => ({ id: r.id, name: r.name, count: r.covered_count ?? 0, badge: r.enabled ? undefined : "Paused" }));
  }, [rulesQ.data, appId]);
  const ruleName = useMemo(() => new Map((rulesQ.data ?? []).map((r) => [r.id, r.name])), [rulesQ.data]);

  const metricEnv = useMemo<MetricEnv>(() => ({
    level: LEVEL[grain],
    eventTypes: eventsQ.data?.rows,
    eventMetricKinds: eventsQ.data?.metric_kinds,
  }), [grain, eventsQ.data]);

  // TZ §8: prune a stored filter against what exists now, once the lists
  // have loaded; write back the cleaned version.
  useEffect(() => {
    if (!eventsQ.data || !countriesQ.data || !rulesQ.data) return;
    const known = new Set<string>([...METRICS]);
    if (grain !== "campaigns") for (const m of KW_AG_METRICS) known.add(m.value);
    for (const m of ["Share of Voice", "SOV slot 2", "SOV total"]) known.delete(m);
    for (const kind of eventsQ.data.metric_kinds ?? [{ prefix: "event:", levels: ["campaign", "ad_group", "keyword"] }]) {
      if (!kind.levels.includes(LEVEL[grain])) continue;
      for (const e of eventsQ.data.rows) known.add(`${kind.prefix}${e.value}`);
    }
    const { filter, changed } = pruneFilter(applied, {
      metrics: known,
      labelIds: new Set(labelOptions.map((l) => l.id)),
      countries: new Set(countriesQ.data.map((c) => c.code)),
      // Deleted rules are absent from the account list (fix2 §5).
      ruleIds: new Set(rulesQ.data.map((r) => r.id)),
    });
    if (changed) { setApplied(filter); saveFilter(key, filter); }
    // `applied` is deliberately not a dependency: prune once per data load,
    // not on every apply (which is already normalized).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsQ.data, countriesQ.data, rulesQ.data, labelOptions, key, grain]);

  const commit = useCallback((f: AdsManagerFilter) => {
    setApplied(f);
    saveFilter(key, f);
  }, [key]);

  const countries = useMemo(() => countriesQ.data ?? [], [countriesQ.data]);
  const countryName = useMemo(() => new Map(countries.map((c) => [c.code, c.name])), [countries]);
  const describe = useCallback((tab: FilterTab) =>
    describeType(applied, tab, {
      metric: (k) => metricLabel(k, eventsQ.data?.rows ?? []),
      label: (id) => labelName.get(id) ?? `#${id}`,
      country: (iso) => countryName.get(iso) ?? iso,
      rule: (id) => ruleName.get(id) ?? `#${id}`,
    }), [applied, eventsQ.data, labelName, countryName, ruleName]);

  const labelsByEntity = useMemo(() => {
    const m = new Map<number, Set<number>>();
    if (entityType) {
      for (const l of labels) {
        for (const r of l.applied_to) {
          if (r.entity_type !== entityType) continue;
          const s = m.get(r.entity_id) ?? new Set<number>();
          s.add(l.id); m.set(r.entity_id, s);
        }
      }
    }
    return m;
  }, [labels, entityType]);
  const EMPTY = useMemo(() => new Set<number>(), []);

  const value = useMemo<Ctx>(() => ({
    grain,
    applied,
    counts: countsOf(applied),
    isActive: !isEmptyFilter(applied),
    apiParam: toApiParam(applied),
    openModal: (tab) => setModal({ open: true, tab }),
    removeType: (tab) => commit(clearType(applied, tab)),
    reset: () => { setApplied(emptyFilter()); clearStoredFilter(key); },
    describe,
    labelIdsOf: (id) => labelsByEntity.get(id) ?? EMPTY,
    splitAvailable: splitAvail,
    splitOn,
    setSplit,
    showChips: !isEmptyFilter(applied) || splitOn,
  }), [grain, applied, commit, key, describe, labelsByEntity, EMPTY, splitAvail, splitOn, setSplit]);

  return (
    <AdsFilterContext.Provider value={value}>
      {children}
      <FilterModal
        open={modal.open}
        initialTab={modal.tab}
        applied={applied}
        metricEnv={metricEnv}
        labels={labelOptions}
        countries={countries}
        rules={ruleOptions}
        onApply={(f) => { commit(f); setModal((m) => ({ ...m, open: false })); }}
        onReset={() => { value.reset(); setModal((m) => ({ ...m, open: false })); }}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
      />
    </AdsFilterContext.Provider>
  );
}

function metricLabel(key: string, events: readonly { value: string; label: string }[]): string {
  const m = /^(event(?:_cpa|_cr)?:)(.+)$/.exec(key);
  if (!m) return key;
  const ev = events.find((e) => e.value === m[2]);
  const base = ev?.label ?? m[2];
  return m[1] === "event_cpa:" ? `${base} CPA` : m[1] === "event_cr:" ? `${base} CR` : base;
}
